import React, { useEffect } from 'react';
import toast, { Toaster, useToasterStore } from 'react-hot-toast';
import useIsTestVersion from '../hooks/useIsTestVersion';

/**
 * App-wide react-hot-toast container, aware of the redesign experiment.
 *
 * Old look: identical to the original inline <Toaster> in App.js
 * (bottom-center, nudged 180px up above the legacy bottom bars).
 *
 * New look: toasts move to the top-center so they never collide with the
 * bottom-anchored floating UI (mobile bottom nav, "Your box" pill, the fixed
 * filter toolbar on desktop). Styling matches the redesign's card language:
 * white rounded card, hairline ring, soft brand-tinted shadow, #544947 text.
 * The `wg-toast` class lets index.css collapse the enter/exit animation for
 * prefers-reduced-motion users.
 *
 * Dedupe (both looks): calls routed through utils/notification get a stable
 * per-message toast id so re-dispatching the same message refreshes the
 * existing toast in place. As a safety net for direct toast.* call sites,
 * we also watch the toaster store and drop older visible toasts whose
 * type+message matches a newer one, so identical toasts never stack.
 */

const NEW_LOOK_TOAST_STYLE = {
  fontFamily: "'Apercu', sans-serif",
  fontSize: '14px',
  lineHeight: '1.35',
  fontWeight: 500,
  textTransform: 'none',
  color: '#544947',
  background: '#ffffff',
  borderRadius: '16px',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  boxShadow: '0 12px 32px -12px rgba(84, 73, 71, 0.28), 0 2px 8px rgba(84, 73, 71, 0.08)',
  padding: '10px 14px',
  maxWidth: 'min(92vw, 420px)',
};

/** Drop older visible duplicates (same type + string message) so identical
 * toasts never render stacked; the newest keeps its full duration. */
const useDedupeToasts = () => {
  const { toasts } = useToasterStore();

  useEffect(() => {
    const seen = new Set();
    // Store is newest-first: keep the first of each type+message, remove the rest.
    toasts
      .filter((t) => t.visible && typeof t.message === 'string')
      .forEach((t) => {
        const key = `${t.type}:${t.message}`;
        if (seen.has(key)) {
          toast.remove(t.id);
        } else {
          seen.add(key);
        }
      });
  }, [toasts]);
};

const AppToaster = () => {
  const isTest = useIsTestVersion();
  useDedupeToasts();

  if (!isTest) {
    return (
      <Toaster
        position="bottom-center"
        containerStyle={{
          bottom: 180,
        }}
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "'Apercu', sans-serif",
          },
        }}
      />
    );
  }

  return (
    <Toaster
      position="top-center"
      gutter={10}
      containerStyle={{
        // Sit just below the sticky top rail so toasts never hide behind its
        // controls. StickyRail publishes its live height while stuck (the
        // category rail with emoji pills is taller than the plain search
        // row); 68px is the fallback for pages without a stuck rail.
        top: 'calc(env(safe-area-inset-top, 0px) + var(--wg-sticky-rail-h, 68px) + 8px)',
      }}
      toastOptions={{
        duration: 3500,
        className: 'wg-toast',
        style: NEW_LOOK_TOAST_STYLE,
        success: {
          iconTheme: {
            primary: '#544947',
            secondary: '#fffdf8',
          },
        },
        error: {
          iconTheme: {
            primary: '#FF6247',
            secondary: '#fffdf8',
          },
        },
        loading: {
          iconTheme: {
            primary: '#544947',
            secondary: 'rgba(84, 73, 71, 0.2)',
          },
        },
      }}
    />
  );
};

export default AppToaster;
